/**
 * modulesCatalog — Central registry of all PERFORM modules.
 *
 * Each module maps to a feature flag and a route.
 * Sections: advanced (core unlocked), extra (optional), future (coming soon).
 *
 * @since Module Discovery System v1
 */

export type ModuleStatus = "active" | "available" | "locked" | "coming_soon";
export type ModuleSection = "advanced" | "extra" | "future";

export interface ModuleDefinition {
  id: string;
  name: string;
  emoji: string;
  description: string;
  longDescription: string;
  pros: string[];
  limits: string[];
  section: ModuleSection;
  flag: string;
  route: string;
  unlockCondition?: {
    type: "merits" | "sessions" | "manual" | "auto";
    value?: number;
    label: string;
  };
  /** Alternative unlock via challenge completion */
  challengeCondition?: {
    label: string;
    challengeId?: string;
  };
  tutorialAvailable: boolean;
  tags: string[];
  relatedModules?: string[];
}

export const MODULES_CATALOG: ModuleDefinition[] = [
  // ── SECTION: advanced (core — unlocked for Advanced users) ──

  {
    id: "achievements",
    name: "Succès & Badges",
    emoji: "🏆",
    description: "Suis ta progression à travers des succès et badges de complétion.",
    longDescription:
      "Le système de succès récompense ta régularité, tes records et ta progression globale. Chaque badge débloqué marque une étape dans ton parcours d'athlète.",
    pros: [
      "Motivation par la progression visible",
      "Historique de tes accomplissements",
      "Badges partagés dans la communauté",
    ],
    limits: ["Les badges sont cosmétiques, pas de bonus en mérites"],
    section: "advanced",
    flag: "ACHIEVEMENTS",
    route: "/achievements",
    tutorialAvailable: true,
    tags: ["succès", "badges", "progression", "motivation", "gamification"],
  },
  {
    id: "billing",
    name: "Mérites & Économie",
    emoji: "⚡",
    description: "Gère tes mérites, consulte ton historique et tes transactions.",
    longDescription:
      "Les mérites sont la monnaie interne de PERFORM. Gagne-les en t'entraînant régulièrement et dépense-les dans la boutique pour débloquer du contenu premium.",
    pros: [
      "Suivi transparent de tes gains et dépenses",
      "Récompenses liées à l'activité réelle",
      "Pas de paiement réel — uniquement du mérite",
    ],
    limits: ["Les mérites ne sont pas convertibles en argent réel"],
    section: "advanced",
    flag: "BILLING_STRIPE",
    route: "/billing",
    tutorialAvailable: true,
    tags: ["mérites", "monnaie", "économie", "récompenses", "points"],
  },
  {
    id: "wellness",
    name: "Nutrition & Bien-être",
    emoji: "🥗",
    description: "Plans nutritionnels personnalisés et suivi bien-être.",
    longDescription:
      "Un module complet pour gérer ton alimentation en fonction de tes objectifs sportifs. Profils nutritionnels, plans générés par le Cerveau, et suivi de tes habitudes.",
    pros: [
      "Plans adaptés à ton profil athlète",
      "Prise en compte des allergies et préférences",
      "Suivi intégré au planning d'entraînement",
    ],
    limits: ["Les plans ne remplacent pas un suivi médical professionnel"],
    section: "advanced",
    flag: "NUTRITION_PLANNING",
    route: "/wellness",
    tutorialAvailable: true,
    tags: ["nutrition", "alimentation", "bien-être", "régime", "santé"],
  },
  {
    id: "library",
    name: "Bibliothèque Scientifique",
    emoji: "📚",
    description: "Accède à 7 000+ faits scientifiques validés sur l'entraînement.",
    longDescription:
      "La bibliothèque regroupe des milliers de faits scientifiques issus de sources vérifiées. Recherche sémantique, filtres par thème, et discussions communautaires sur les contenus.",
    pros: [
      "Contenu sourcé et validé par des experts",
      "Recherche sémantique avancée",
      "Discussions communautaires sur chaque fait",
    ],
    limits: ["Contenu en Français uniquement pour l'instant"],
    section: "advanced",
    flag: "ACHIEVEMENTS", // Library is always available in advanced
    route: "/library",
    tutorialAvailable: true,
    tags: ["science", "faits", "recherche", "bibliothèque", "sources"],
  },
  {
    id: "community",
    name: "Communauté",
    emoji: "👥",
    description: "Partage tes séances et programmes avec la communauté PERFORM.",
    longDescription:
      "Publie tes créations, découvre celles des autres athlètes, et engage-toi dans des discussions constructives autour de l'entraînement.",
    pros: [
      "Partage en un clic depuis tes séances",
      "Likes, commentaires et sauvegardes",
      "Contenu promu automatiquement si populaire",
    ],
    limits: ["Modération active — contenu inadapté signalé"],
    section: "advanced",
    flag: "ACHIEVEMENTS", // Community is core
    route: "/community",
    tutorialAvailable: false,
    tags: ["communauté", "social", "partage", "athlètes"],
  },
  {
    id: "questionnaires",
    name: "Questionnaires",
    emoji: "📋",
    description: "Réponds aux questionnaires de suivi assignés par ton coach.",
    longDescription:
      "Les questionnaires permettent à ton coach de recueillir des informations structurées sur ton état, ta récupération ou tes préférences. Tout est centralisé dans ton profil.",
    pros: [
      "Communication structurée avec ton encadrement",
      "Historique des réponses accessible",
      "Notifications automatiques",
    ],
    limits: ["Nécessite un coach ou professionnel de santé actif"],
    section: "advanced",
    flag: "QUESTIONNAIRE_HUB",
    route: "/questionnaires",
    tutorialAvailable: false,
    tags: ["questionnaire", "coach", "suivi", "formulaire"],
  },
  {
    id: "program-export",
    name: "Export PDF Programme",
    emoji: "📄",
    description: "Exporte tes programmes d'entraînement en PDF partageable.",
    longDescription:
      "Génère un PDF structuré de tes programmes pour les imprimer, les partager par email, ou les conserver comme référence hors ligne.",
    pros: [
      "PDF professionnel et lisible",
      "Partage facile par email ou impression",
      "Fonctionne hors connexion",
    ],
    limits: ["Export limité au programme sélectionné"],
    section: "advanced",
    flag: "PROGRAM_EXPORT_PDF",
    route: "/programs",
    tutorialAvailable: false,
    tags: ["export", "pdf", "programme", "impression", "partage"],
  },
  {
    id: "calendar-sync",
    name: "Sync iCal",
    emoji: "📅",
    description: "Synchronise ton planning d'entraînement avec ton calendrier.",
    longDescription:
      "Exporte tes séances planifiées au format iCal pour les intégrer à Apple Calendar, Outlook ou tout autre calendrier compatible.",
    pros: [
      "Compatible avec tous les calendriers iCal",
      "Mise à jour automatique",
      "Vision unifiée de ton agenda",
    ],
    limits: ["Synchronisation unidirectionnelle (PERFORM → calendrier)"],
    section: "advanced",
    flag: "CALENDAR_SYNC_ICAL",
    route: "/calendar/sync",
    tutorialAvailable: false,
    tags: ["calendrier", "ical", "sync", "planning", "agenda"],
  },

  // ── SECTION: extra (optional modules, some locked) ──

  {
    id: "nutrition-supplements",
    name: "Compléments Alimentaires",
    emoji: "💊",
    description: "Guide personnalisé de supplémentation sportive.",
    longDescription:
      "Un assistant basé sur la science pour identifier les compléments pertinents selon ton profil, tes objectifs et ton alimentation actuelle.",
    pros: [
      "Recommandations basées sur la science",
      "Adapté à ton profil nutritionnel",
      "Alertes sur les interactions",
    ],
    limits: [
      "Ne remplace pas un avis médical",
      "Certains compléments ont des preuves limitées",
    ],
    section: "extra",
    flag: "NUTRITION_SUPPLEMENTS",
    route: "/nutrition/supplements",
    unlockCondition: {
      type: "merits",
      value: 200,
      label: "Débloque avec 200 mérites ⚡",
    },
    challengeCondition: {
      label: "Complète le défi « 7 jours de nutrition » pour débloquer",
      challengeId: "nutrition_7d",
    },
    tutorialAvailable: false,
    tags: ["compléments", "suppléments", "nutrition", "vitamines", "créatine"],
    relatedModules: ["wellness"],
  },
  {
    id: "science-cards",
    name: "Cartes Science",
    emoji: "🔬",
    description: "Collecte et explore des cartes de faits scientifiques rares.",
    longDescription:
      "Un système de collecte ludique de faits scientifiques présentés sous forme de cartes à collectionner. Découvre des connaissances cachées sur l'entraînement.",
    pros: [
      "Apprentissage gamifié et engageant",
      "Faits vérifiés et sourcés",
      "Cartes communes, rares et légendaires",
    ],
    limits: ["Les cartes n'ont pas d'effet sur l'entraînement"],
    section: "extra",
    flag: "SCIENCE_CARDS",
    route: "/science-cards",
    unlockCondition: {
      type: "merits",
      value: 500,
      label: "Débloque avec 500 mérites ⚡",
    },
    challengeCondition: {
      label: "Complète le défi « Contributeur scientifique » pour débloquer",
      challengeId: "science_contributor",
    },
    tutorialAvailable: false,
    tags: ["science", "cartes", "collection", "faits", "gamification"],
    relatedModules: ["library"],
  },
  {
    id: "social-friends",
    name: "Profil Social & Amis",
    emoji: "🤝",
    description: "Connecte-toi avec d'autres athlètes et suis leur progression.",
    longDescription:
      "Crée ton profil social, ajoute des amis, et suis leur activité. Compare tes statistiques et motivez-vous mutuellement.",
    pros: [
      "Connexion avec d'autres athlètes",
      "Comparaison anonymisée des performances",
      "Fil d'activité de tes amis",
    ],
    limits: [
      "Respect strict de la vie privée — données partagées au choix",
    ],
    section: "extra",
    flag: "SOCIAL_FRIENDS",
    route: "/social/friends",
    unlockCondition: {
      type: "sessions",
      value: 20,
      label: "Complète 20 séances pour débloquer",
    },
    challengeCondition: {
      label: "Complète le défi « Réseau social » pour débloquer",
      challengeId: "social_network",
    },
    tutorialAvailable: false,
    tags: ["social", "amis", "profil", "connexion", "communauté"],
    relatedModules: ["community"],
  },
  {
    id: "progression-charts",
    name: "Graphiques Long Terme",
    emoji: "📈",
    description: "Visualise ta progression sur plusieurs semaines et mois.",
    longDescription:
      "Des graphiques détaillés montrant l'évolution de tes performances, volumes d'entraînement, et indicateurs clés sur le long terme.",
    pros: [
      "Vision macro de ta progression",
      "Détection de plateaux et tendances",
      "Export des données possible",
    ],
    limits: ["Nécessite un historique d'au moins 2 semaines"],
    section: "extra",
    flag: "PROGRESSION_CHARTS",
    route: "/progression/charts",
    tutorialAvailable: false,
    tags: ["graphiques", "progression", "statistiques", "évolution", "tendances"],
  },
  {
    id: "merit-shop",
    name: "Boutique Mérites",
    emoji: "🛍️",
    description: "Dépense tes mérites pour débloquer du contenu et des bonus.",
    longDescription:
      "La boutique te permet d'échanger tes mérites gagnés contre des déverrouillages de modules, des thèmes visuels, et d'autres bonus exclusifs.",
    pros: [
      "Récompenses tangibles pour ton engagement",
      "Nouveaux items ajoutés régulièrement",
      "Pas de paiement réel requis",
    ],
    limits: ["Stock limité sur certains items exclusifs"],
    section: "extra",
    flag: "MERIT_SHOP",
    route: "/shop",
    tutorialAvailable: false,
    tags: ["boutique", "shop", "mérites", "achat", "récompenses"],
    relatedModules: ["billing"],
  },
  {
    id: "challenges",
    name: "Défis & Challenges",
    emoji: "🏅",
    description: "Participe à des défis communautaires et mesure-toi aux autres.",
    longDescription:
      "Rejoins des défis temporaires avec des objectifs précis. Compare ta progression avec celle des autres participants et grimpe dans le classement.",
    pros: [
      "Motivation par la compétition saine",
      "Défis variés et renouvelés",
      "Récompenses en mérites pour les participants",
    ],
    limits: ["Les défis ont une durée limitée", "Classement anonymisé"],
    section: "extra",
    flag: "CHALLENGES_UI",
    route: "/challenges",
    unlockCondition: {
      type: "merits",
      value: 300,
      label: "Débloque avec 300 mérites ⚡",
    },
    challengeCondition: {
      label: "Complète le défi « Série de 7 jours » pour débloquer",
      challengeId: "streak_7d",
    },
    tutorialAvailable: false,
    tags: ["défis", "challenges", "compétition", "classement", "communauté"],
    relatedModules: ["community"],
  },

  // ── SECTION: future (coming soon) ──

  {
    id: "planning-central",
    name: "Planning Central",
    emoji: "🗓️",
    description: "Vue unifiée de tous tes plannings et calendriers.",
    longDescription: "Un hub centralisé pour visualiser et gérer l'ensemble de tes activités planifiées.",
    pros: ["Vue d'ensemble complète", "Gestion multi-calendriers"],
    limits: [],
    section: "future",
    flag: "BRAIN_ROUTER",
    route: "/planning-central",
    tutorialAvailable: false,
    tags: ["planning", "calendrier", "vue", "central"],
  },
  {
    id: "brain-router",
    name: "Cerveau Orchestrateur",
    emoji: "🧠",
    description: "Le Cerveau coordonne automatiquement tes modules et suggestions.",
    longDescription: "Un système intelligent qui analyse ton activité et propose des actions pertinentes au bon moment.",
    pros: ["Suggestions contextuelles", "Coordination automatique"],
    limits: [],
    section: "future",
    flag: "BRAIN_ROUTER",
    route: "/brain/router",
    tutorialAvailable: false,
    tags: ["cerveau", "intelligence", "orchestrateur", "suggestions"],
  },
  {
    id: "avatar",
    name: "Avatar de Projection",
    emoji: "🧍",
    description: "Visualise ta progression physique à travers un avatar personnalisé.",
    longDescription: "Un avatar 3D qui évolue en fonction de tes performances et de ta progression.",
    pros: ["Visualisation motivante", "Évolution en temps réel"],
    limits: [],
    section: "future",
    flag: "AVATAR",
    route: "/avatar",
    tutorialAvailable: false,
    tags: ["avatar", "projection", "visuel", "3d", "progression"],
  },
  {
    id: "xp-system",
    name: "Niveaux & XP",
    emoji: "⭐",
    description: "Système de niveaux et d'expérience pour suivre ta maturité d'athlète.",
    longDescription: "Gagne de l'XP en t'entraînant et monte de niveau pour débloquer de nouvelles fonctionnalités.",
    pros: ["Progression visible et motivante", "Déblocages progressifs"],
    limits: [],
    section: "future",
    flag: "XP_SYSTEM",
    route: "/progression/xp",
    tutorialAvailable: false,
    tags: ["xp", "niveaux", "expérience", "progression"],
  },
  {
    id: "pro-marketplace",
    name: "Marketplace Pro",
    emoji: "🏪",
    description: "Trouve des coachs et professionnels certifiés PERFORM.",
    longDescription: "Un annuaire de professionnels du sport et de la santé intégrés à la plateforme.",
    pros: ["Professionnels vérifiés", "Intégration directe"],
    limits: [],
    section: "future",
    flag: "PRO_MARKETPLACE",
    route: "/pro/marketplace",
    tutorialAvailable: false,
    tags: ["marketplace", "coach", "professionnel", "pro"],
  },
  {
    id: "calendar-google",
    name: "Google Calendar",
    emoji: "📆",
    description: "Synchronisation bidirectionnelle avec Google Calendar.",
    longDescription: "Connecte ton compte Google pour une synchronisation automatique de tes séances.",
    pros: ["Synchronisation bidirectionnelle", "Mise à jour en temps réel"],
    limits: [],
    section: "future",
    flag: "CALENDAR_SYNC_GOOGLE",
    route: "/calendar/google",
    tutorialAvailable: false,
    tags: ["google", "calendrier", "sync", "agenda"],
  },
  {
    id: "brain-sessions",
    name: "Sessions Cerveau",
    emoji: "💡",
    description: "Sessions de coaching guidées par le Cerveau.",
    longDescription: "Des sessions interactives où le Cerveau t'accompagne dans ta réflexion d'entraînement.",
    pros: ["Coaching personnalisé", "Analyse contextuelle"],
    limits: [],
    section: "future",
    flag: "BRAIN_SESSIONS",
    route: "/brain/sessions",
    tutorialAvailable: false,
    tags: ["cerveau", "sessions", "coaching", "guidé"],
  },
  {
    id: "groupes",
    name: "Groupes",
    emoji: "🏋️",
    description: "Crée et rejoins des groupes d'entraînement entre athlètes.",
    longDescription:
      "Les groupes permettent de s'organiser entre athlètes partageant les mêmes objectifs. Planifiez ensemble, partagez vos résultats et motivez-vous mutuellement.",
    pros: [
      "Organisation collective de l'entraînement",
      "Chat de groupe intégré",
      "Partage de séances entre membres",
    ],
    limits: ["Nécessite le module social activé"],
    section: "extra",
    flag: "SOCIAL_FRIENDS",
    route: "/groups",
    unlockCondition: {
      type: "sessions",
      value: 10,
      label: "Complète 10 séances pour débloquer",
    },
    challengeCondition: {
      label: "Complète le défi « Esprit d'équipe » pour débloquer",
      challengeId: "team_spirit",
    },
    tutorialAvailable: false,
    tags: ["groupes", "équipe", "social", "entraînement", "collectif"],
    relatedModules: ["social-friends", "community"],
  },
  {
    id: "coach-tools",
    name: "Espace Coach",
    emoji: "🏋️‍♂️",
    description: "Gère tes athlètes, planifie des séances et suis leur progression.",
    longDescription:
      "L'espace Coach regroupe tous les outils de gestion d'athlètes : création de personas, assignation de séances, suivi de progression et communication directe.",
    pros: [
      "Vue d'ensemble de tous tes athlètes",
      "Planification de séances collectives",
      "Suivi individuel et groupé",
    ],
    limits: ["Réservé aux utilisateurs avec le rôle Coach"],
    section: "extra",
    flag: "ACHIEVEMENTS",
    route: "/coach",
    tutorialAvailable: false,
    tags: ["coach", "entraîneur", "athlètes", "gestion", "suivi"],
    relatedModules: ["community"],
  },

  {
    id: "guide",
    name: "Guide",
    emoji: "📖",
    description: "Tutos, conseils et formation pour bien démarrer sur PERFORM.",
    longDescription:
      "Le Guide PERFORM regroupe les tutoriels de chaque module, les conseils d'utilisation et les ressources de formation pour tirer le meilleur de la plateforme.",
    pros: ["Tutoriels par module", "Conseils pratiques", "Ressources de formation"],
    limits: [],
    section: "extra",
    flag: "ACHIEVEMENTS",
    route: "/guide",
    tutorialAvailable: false,
    tags: ["guide", "tuto", "formation", "aide"],
  },
];

